// src/pages/projects/components/TeamMemberDialog.jsx
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  Typography,
  FormControlLabel,
  Checkbox
} from '@mui/material';

import { TEAM_ROLES } from '../../../constants/teamConstants';

/**
 * Dialog for adding or editing a team member
 */
const TeamMemberDialog = ({
  open,
  onClose,
  member,
  onMemberChange,
  onSave,
  availableUsers = [],
  isLoading,
  mode = 'add' // 'add' or 'edit'
}) => {
  // Mode-specific properties
  const isEditMode = mode === 'edit';
  const dialogTitle = isEditMode ? 'Edit Team Member' : 'Add Team Member';
  const saveButtonText = isEditMode ? 'Save Changes' : 'Add Member';
  const saveButtonDisabled = isEditMode ? isLoading : !member.user || isLoading;

  // Handle field change
  const handleFieldChange = (field, value) => {
    onMemberChange(field, value);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {isEditMode && member?.user ? (
            <>
              <Typography variant="subtitle1" gutterBottom>
                {member.user?.firstName} {member.user?.lastName}
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                {member.user?.email}
              </Typography>
            </>
          ) : (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="user-select-label">User</InputLabel>
              <Select
                labelId="user-select-label"
                id="user-select"
                value={member.user || ''}
                onChange={(e) => handleFieldChange('user', e.target.value)}
                label="User"
                disabled={isLoading || isEditMode}
              >
                {availableUsers.map(user => (
                  <MenuItem key={user._id} value={user._id}>
                    {user.firstName} {user.lastName} ({user.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="role-select-label">Role</InputLabel>
            <Select
              labelId="role-select-label"
              id="role-select"
              value={member.role || ''}
              onChange={(e) => handleFieldChange('role', e.target.value)}
              label="Role"
            >
              {TEAM_ROLES.map(role => (
                <MenuItem key={role.value} value={role.value}>
                  {role.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Typography variant="subtitle2" gutterBottom>
            Permissions
          </Typography>
          
          <FormControlLabel
            control={
              <Checkbox
                checked={member.permissions?.canEdit || false}
                onChange={(e) => handleFieldChange('permissions.canEdit', e.target.checked)}
              />
            }
            label="Can Edit Project"
          />
          
          <FormControlLabel
            control={
              <Checkbox
                checked={member.permissions?.canManageTasks || false}
                onChange={(e) => handleFieldChange('permissions.canManageTasks', e.target.checked)}
              />
            }
            label="Can Manage Tasks"
          />
          
          <FormControlLabel
            control={
              <Checkbox
                checked={member.permissions?.canComment || false}
                onChange={(e) => handleFieldChange('permissions.canComment', e.target.checked)}
              />
            }
            label="Can Comment"
          />
          
          <FormControlLabel
            control={
              <Checkbox
                checked={member.permissions?.canViewFiles || false}
                onChange={(e) => handleFieldChange('permissions.canViewFiles', e.target.checked)}
              />
            }
            label="Can View Files"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={onSave} 
          variant="contained" 
          disabled={saveButtonDisabled}
        >
          {saveButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TeamMemberDialog;